import { getNoteFrequency, getNoteFrequencyLog2 } from "../helpers/Notes";
import { ISynth } from "./abstraction/ISynth";


interface IOscillator {
    weight: number;
    delay: number;
    frequency: number;
    oscillatorNode: OscillatorNode;
    gainNode: GainNode;
    delayNode: DelayNode;
}

export class OscillatorSynth implements ISynth {

    protected _isOpen: boolean;
    protected _ctx: AudioContext;
    protected _mainGain: GainNode;
    protected _volume: number;
    protected _oscillators: IOscillator[];

    constructor() {
        this.smooth = 0.01;
        this._volume = 0.7;
    }

    async openAsync(): Promise<void> {

        if (this._isOpen)
            return;

        this._ctx = new AudioContext();

        this._oscillators = [];

        this._mainGain = this._ctx.createGain();
        this._mainGain.gain.value = this._volume;
            
        const wave = this.createWave();

        this.createOscillator(0.5, 0, undefined, wave);
        //this.createOscillator(1, 0.05, undefined, wave);
        this.createOscillator(0.2, 0, 3);

        this.setGains();

        this._mainGain.connect(this._ctx.destination);

        this._isOpen = true;
    }

    protected createOscillator(weight: number, delay = 0, frequency: number = undefined, wave?: PeriodicWave) {

        const result: IOscillator = {
            delay,
            weight,
            frequency,
            oscillatorNode: this._ctx.createOscillator(),
            delayNode: delay ? this._ctx.createDelay() : undefined,
            gainNode: this._ctx.createGain(),
        }

        if (result.delayNode) {
            result.oscillatorNode.connect(result.delayNode);
            result.delayNode.connect(result.gainNode);
            result.delayNode.delayTime.value = delay;
        }
        else
            result.oscillatorNode.connect(result.gainNode);

        result.gainNode.connect(this._mainGain);

        if (wave)
            result.oscillatorNode.setPeriodicWave(wave);

        result.oscillatorNode.frequency.value = 0;
        result.oscillatorNode.start();

        this._oscillators.push(result);

        return result;
    }

    protected setGains() {
        let sum = 0;
        this._oscillators.forEach(a => sum += a.weight);
        this._oscillators.forEach(a => a.gainNode.gain.value = a.weight);
    }

    protected setFrequency(value: number, time: number, volume?: number) {

        if (volume !== undefined)
            this._mainGain.gain.setTargetAtTime(volume * this._volume, time, this.smooth);

        this._oscillators.forEach(a => { 
           a.oscillatorNode.frequency.setTargetAtTime(a.frequency ?? value, time, this.smooth);
        });
    }
     
    protected createWave() {

        const real = [0, 1, 0.5, 0.25, 0, 0.1];
        const imag = [0, 0, 1, 0, 0, 0];

        return this._ctx.createPeriodicWave(real, imag);
    }

    async closeAsync(): Promise<void> {
        if (!this._isOpen)
            return;
        this._ctx.close();
        this._ctx = undefined;
        this._isOpen = false;
    }

    async resetAsync() {
        await this.closeAsync();
        await this.openAsync();
    }

    async setVolumeAsync(value: number) {
        this._volume = value;
    }

    getTime(relTime: number) {
        return (relTime - performance.now() / 1000) + this._ctx.currentTime;
    }

    playNote(note: number, volume: number, duration: number, startTime?: number): void {

        if (!this._ctx)
            return;

        const freq = getNoteFrequency(note);
        const time = this.getTime(startTime);

        this.setFrequency(freq, time, volume);
        this.setFrequency(0, time + duration);
    }

    noteOn(note: number, volume: number): void {

        this.setFrequency(getNoteFrequency(note), this._ctx.currentTime);
    }

    noteOff(note?: number, startTime?: number): void {

        if (!this._ctx)
            return;

        if (startTime)
            this.setFrequency(0, this.getTime(startTime));
        else {
            if (note)
                this.setFrequency(0, this._ctx.currentTime);
            else
                this.resetAsync();
        }
    }

    get deviceId() {
        return "Oscillator";
    }


    smooth: number;

}